import { Segment } from "@heroui-pro/react/segment";
import { memo, useState } from "react";
import type { Key } from "react-aria-components";
import { PresetPill } from "@funnel-ui/primitives/PresetPill";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

export type PickerGroup = {
  id: string;
  label: string;
  options: { id: string; label: string }[];
  /** Allow multi-select within this group. Defaults to single. */
  multi?: boolean;
};

type MultiPickerGroupedProps = {
  question: string;
  description?: string;
  groups: PickerGroup[];
  initialValue?: Record<string, string | string[]>;
  onChange?: (value: Record<string, string | string[]>) => void;
  className?: string;
  testId?: string;
};

type SingleGroupProps = {
  group: PickerGroup;
  selectedKey: string | undefined;
  onUpdate: (groupId: string, next: string) => void;
};

const SingleGroup = ({ group, selectedKey, onUpdate }: SingleGroupProps) => {
  const handleSelectionChange = (key: Key): void => {
    onUpdate(group.id, String(key));
  };
  return (
    <div className="-mx-5 overflow-x-auto px-5 pb-1">
      <Segment
        aria-label={group.label}
        size="sm"
        selectedKey={selectedKey ?? null}
        onSelectionChange={handleSelectionChange}
      >
        {group.options.map((option) => (
          <Segment.Item key={option.id} id={option.id}>
            {option.label}
          </Segment.Item>
        ))}
      </Segment>
    </div>
  );
};

type MultiGroupProps = {
  group: PickerGroup;
  selected: string[];
  onToggle: (groupId: string, optionId: string) => void;
};

const MultiGroup = ({ group, selected, onToggle }: MultiGroupProps) => (
  <div role="group" aria-label={group.label} className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1">
    {group.options.map((option) => {
      const isSelected = selected.includes(option.id);
      return (
        <PresetPill
          key={option.id}
          label={option.label}
          selected={isSelected}
          ariaPressed={isSelected}
          onPress={() => onToggle(group.id, option.id)}
        />
      );
    })}
  </div>
);

const MultiPickerGroupedImpl = ({
  question,
  description,
  groups,
  initialValue = {},
  onChange,
  className,
  testId,
}: MultiPickerGroupedProps) => {
  const [value, setValue] = useState<Record<string, string | string[]>>(initialValue);

  const update = (groupId: string, next: string | string[]): void => {
    const merged = { ...value, [groupId]: next };
    setValue(merged);
    onChange?.(merged);
  };

  const toggleMultiOption = (groupId: string, optionId: string): void => {
    const current = value[groupId];
    const arr = Array.isArray(current) ? current : [];
    const next = arr.includes(optionId) ? arr.filter((id) => id !== optionId) : [...arr, optionId];
    update(groupId, next);
  };

  return (
    <section data-testid={testId} className={`flex flex-col gap-6 ${className ?? ""}`}>
      <StepHeading title={question} description={description} editablePath="question" />
      {groups.map((group) => (
        <div key={group.id} className="flex flex-col gap-2">
          <span
            className="text-sm font-medium"
            style={{ color: "hsl(var(--brand-bg-foreground))" }}
          >
            {group.label}
            {group.multi && (
              <span
                className="ml-1 text-xs font-normal"
                style={{ color: "hsl(var(--hero-fg-muted))" }}
              >
                (pick any)
              </span>
            )}
          </span>
          {group.multi ? (
            <MultiGroup
              group={group}
              selected={Array.isArray(value[group.id]) ? (value[group.id] as string[]) : []}
              onToggle={toggleMultiOption}
            />
          ) : (
            <SingleGroup
              group={group}
              selectedKey={
                typeof value[group.id] === "string" && value[group.id] !== ""
                  ? (value[group.id] as string)
                  : undefined
              }
              onUpdate={update}
            />
          )}
        </div>
      ))}
    </section>
  );
};

export const MultiPickerGrouped = memo(MultiPickerGroupedImpl);

export default MultiPickerGrouped;
